import React, { useEffect, useRef, useState } from 'react';
import { LatexText, LatexParagraphs, SIK_HARFLERI, sikIzgaraUygunMu, kokKirp } from './SoruKarti';
import { figurOnculBol } from '../lib/figurOnculBol';
import { dersLabel } from '../lib/dersler';
import { API } from '../lib/ortam';
import { authBaslik } from '../lib/authBaslik';

// KİTAPÇIK GÖRÜNÜMÜ (2026-10-31, kullanıcı isteği): soru listesi bir soru kitapçığı sayfası
// gibi gösterilir — A4 oranlı beyaz sayfa, iki sütun, ortada sütun çizgisi. Kartta yalnız
// soru + şıklar (doğru cevap, çözüm, yorum YOK); tıklanınca mevcut detay açılır. Rozetler
// (ders, puan, numara, durum, müfredat, bağlam, satış, benzerlik) App'teki render
// fonksiyonundan gelir — tek kaynak.
//
// Web sayfası kâğıttan geniştir: sayfa A4 oranını korur ama ekran genişliğine büyütülür
// (en çok ~1000 px). Bir web sayfası (10 soru) birkaç kitapçık sayfasına bölünür.

export const KITAPCIK_SAYFA_SORU = 4; // kitapçık sayfası başına soru (ÖSYM 3-5, KURALLAR §16)

/** Öğeleri (baglamGrupla çıktısı) kitapçık sayfalarına böler; grup bölünmez, üye sayısı kadar yer tutar. */
export function sayfalaraBol(ogeler, kapasite = KITAPCIK_SAYFA_SORU) {
  const sayfalar = [];
  let cari = [];
  let dolu = 0;
  ogeler.forEach((o) => {
    const agirlik = o.__grup ? o.uyeler.length : 1;
    if (cari.length && dolu + agirlik > kapasite) { sayfalar.push(cari); cari = []; dolu = 0; }
    cari.push(o);
    dolu += agirlik;
  });
  if (cari.length) sayfalar.push(cari);
  return sayfalar;
}

// Şekil tembel yüklenir (ekrana yaklaşınca). SVG <img> içinde basılır: aynı sayfada birden çok
// TikZ SVG'si satır içi dururken dvisvgm glyph kimlikleri çakışır (müşteri paneliyle aynı ders).
function KitapcikSekil({ tikz, gorsel, olcek }) {
  const kutu = useRef(null);
  const [gorundu, setGorundu] = useState(false);
  const [durum, setDurum] = useState({ src: null, hata: '' });

  useEffect(() => {
    if (gorundu || !kutu.current) return undefined;
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) { setGorundu(true); return undefined; }
    const io = new window.IntersectionObserver((g) => {
      if (g.some((x) => x.isIntersecting)) { setGorundu(true); io.disconnect(); }
    }, { rootMargin: '300px' });
    io.observe(kutu.current);
    return () => io.disconnect();
  }, [gorundu]);

  useEffect(() => {
    if (!gorundu) return undefined;
    if (gorsel) {
      const src = /^(https?:|data:)/i.test(gorsel) ? gorsel
        : /^\s*</.test(gorsel) ? `data:image/svg+xml;charset=utf-8,${encodeURIComponent(gorsel)}`
          : `data:image/png;base64,${gorsel}`;
      setDurum({ src, hata: '' });
      return undefined;
    }
    let iptal = false;
    fetch(`${API}/api/tikz/render`, { method: 'POST', headers: authBaslik(), body: JSON.stringify({ tikz }) })
      .then((r) => r.json())
      .then((d) => {
        if (iptal) return;
        if (d.basarili && d.svg) setDurum({ src: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(d.svg)}`, hata: '' });
        else setDurum({ src: null, hata: d.hata || 'derlenemedi' });
      })
      .catch((e) => { if (!iptal) setDurum({ src: null, hata: e.message }); });
    return () => { iptal = true; };
  }, [gorundu, tikz, gorsel]);

  return (
    <div ref={kutu} data-kitapcik-sekil className="my-2 text-center">
      {durum.src ? (
        <img alt="Soru şekli" src={durum.src} className="inline-block max-w-full h-auto"
          style={{ maxHeight: Math.round(300 * (olcek || 1)) }} />
      ) : durum.hata ? (
        <div className="text-xs text-orange-700">Şekil gösterilemedi: {durum.hata}</div>
      ) : (
        <div className="text-xs text-gray-400 py-6">Şekil yükleniyor…</div>
      )}
    </div>
  );
}

function olcekOku(v) {
  const n = Number(v);
  return Number.isFinite(n) && n >= 0.3 && n <= 3 ? n : 1;
}

/** Bir sorunun gövdesi + şıkları (kitapçık dizgisi). `kok` verilirse gövdeden kırpılır (bağlam üyesi). */
// sekilCiz(s) (2026-10-31, müşteri paneli): şekli kendi ucundan getiren bileşen; s.__sekil işaretli
// soruda tikz/gorsel_url yerine o basılır (yerleşim aynı: gövde → şekil → öncüller).
function SoruGovde({ s, kok = null, sekilGoster = true, sekilCiz = null }) {
  const metin = kok ? kokKirp(s.soru_metni_latex || '', kok) : (s.soru_metni_latex || '');
  const ozel = sekilGoster && sekilCiz && s.__sekil ? sekilCiz(s) : null;
  const tikz = !ozel && sekilGoster && String(s.diyagram_tikz || '').trim() ? s.diyagram_tikz : null;
  const gorsel = !ozel && sekilGoster && !tikz && s.gorsel_url ? s.gorsel_url : null;
  const bolme = (ozel || tikz || gorsel) ? figurOnculBol(metin) : null;
  const ust = bolme ? bolme.ust : metin;
  const alt = bolme ? bolme.alt : '';
  const harfler = SIK_HARFLERI.filter((h) => String(s[`sik_${h.toLowerCase()}`] ?? '').trim());
  const izgara = sikIzgaraUygunMu(s);
  return (
    <div className="font-serif text-[15px] leading-relaxed text-gray-900">
      {ust && <LatexParagraphs className="mb-1">{ust}</LatexParagraphs>}
      {ozel}
      {(tikz || gorsel) && <KitapcikSekil tikz={tikz} gorsel={gorsel} olcek={olcekOku(s.sekil_olcek)} />}
      {alt && <LatexParagraphs className="mt-1">{alt}</LatexParagraphs>}
      {harfler.length > 0 && (
        <div data-kitapcik-siklar className={`mt-2 ${izgara ? 'grid grid-cols-2 gap-x-4 gap-y-1' : 'flex flex-col gap-1'}`}>
          {harfler.map((h) => (
            <div key={h} className="flex gap-1.5 min-w-0 break-words">
              <span className="font-bold shrink-0">{h})</span>
              <span className="min-w-0"><LatexText>{s[`sik_${h.toLowerCase()}`]}</LatexText></span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * ogeler: baglamGrupla çıktısı (tekil soru ya da {__grup, uyeler}).
 * ilkNo: bu web sayfasındaki ilk sorunun kitapçık numarası ((sayfa-1)*limit + 1).
 * rozetler(s) / grupRozetleri(uyeler): App'in rozet satırları.
 */
// Tema (2026-10-31, LGS "optik form" paneli de kitapçık görünümünü kullanır): yalnız çerçeve,
// başlık ve seçim/üzerine gelme renkleri değişir; dizgi (soru + şıklar, iki sütun) ortak.
const TEMA = {
  yks: {
    sayfa: 'border-gray-300 shadow-md', bant: 'border-gray-800 text-gray-700', kural: '#9ca3af',
    soru: 'hover:bg-blue-50/60', secili: 'ring-2 ring-blue-600 bg-blue-50', sagEtiket: 'Soru Havuzu',
  },
  lgs: {
    sayfa: 'border-lgs-cizgi shadow-sm', bant: 'border-lgs-murekkep text-lgs-soluk font-lexend', kural: '#E1E4E9',
    soru: 'hover:bg-lgs-kagit', secili: 'ring-2 ring-lgs-optik bg-lgs-optikacik', sagEtiket: 'LGS · 8. sınıf',
  },
  // Müşteri (satış) paneli: seçili = sepette (yeşil).
  musteri: {
    sayfa: 'border-gray-300 shadow-md', bant: 'border-gray-800 text-gray-700', kural: '#9ca3af',
    soru: 'hover:bg-blue-50/60', secili: 'ring-2 ring-green-600 bg-green-50/70', sagEtiket: 'Soru Seçimi',
  },
};

export default function KitapcikGorunum({ ogeler, ilkNo = 1, onSoruTikla, onGrupTikla, rozetler, grupRozetleri, seciliIds, topluMod, tema = 'yks', baslikAdi, sekilCiz = null }) {
  const t = TEMA[tema] || TEMA.yks;
  const sayfalar = sayfalaraBol(ogeler);
  let no = ilkNo;
  const numaralar = new Map();
  ogeler.forEach((o) => {
    if (o.__grup) { numaralar.set(o.key, no); no += o.uyeler.length; } else { numaralar.set(o.id, no); no += 1; }
  });
  const dersler = [...new Set(ogeler.flatMap((o) => (o.__grup ? o.uyeler : [o]).map((x) => x.ders)).filter(Boolean))];
  const baslik = baslikAdi ? baslikAdi(dersler) : (dersler.length === 1 ? dersLabel(dersler[0]) : 'Karma');

  if (!ogeler.length) return null;

  return (
    <div data-kitapcik className="flex flex-col items-center gap-6">
      {sayfalar.map((sayfa, si) => (
        // A4 oranı EN AZ yüksekliktir, sabit değil (2026-10-31, kullanıcı: "üst üste binme olmayacak").
        // Sabit aspect-ratio'da sütun alanının yüksekliği belirli oluyor, sığmayan içerik yeni
        // sütunlara açılıp sayfanın dışına (yan sütuna ve alttaki sayfaya) taşıyordu. Şimdi sayfa tek
        // hücreli bir ızgara: ayraç (padding-top %141,4 = 297/210 genişliğin) ile içerik aynı
        // hücrede; satır ikisinden uzun olana uyar, içerik uzunsa sayfa uzar.
        <section key={si} data-kitapcik-sayfa={si + 1}
          className={`w-full max-w-[1000px] bg-white border rounded-sm grid ${t.sayfa}`}>
          <div aria-hidden="true" style={{ gridArea: '1 / 1', paddingTop: `${(297 / 210) * 100}%` }} />
          <div className="flex flex-col min-w-0" style={{ gridArea: '1 / 1' }}>
          {/* Üst bant — ÖSYM kitapçık başlığı gibi */}
          <header className={`flex items-center justify-between px-6 sm:px-10 pt-5 pb-2 border-b-2 text-[11px] sm:text-xs font-semibold tracking-wide uppercase ${t.bant}`}>
            <span>{baslik}</span>
            <span>{t.sagEtiket}</span>
          </header>
          <div className="relative flex-1 px-6 sm:px-10 py-5 sm:columns-2 sm:gap-10"
            style={{ columnRule: `1px solid ${t.kural}`, columnFill: 'balance' }}>
            {sayfa.map((o) => {
              if (o.__grup) {
                const uyeler = o.uyeler;
                const gercek = uyeler.find((u) => !u.__eksik) || uyeler[0];
                const kok = gercek.grup_ortak_kok || null;
                const ilk = numaralar.get(o.key);
                const secili = uyeler.every((u) => seciliIds && seciliIds.has(u.id));
                return (
                  <article key={o.key} data-kitapcik-grup={o.key} onClick={() => onGrupTikla(uyeler)}
                    className={`mb-6 break-inside-avoid cursor-pointer rounded-md p-2 -mx-2 transition-colors ${t.soru} ${secili ? t.secili : ''}`}>
                    <div className="mb-1.5">{grupRozetleri(uyeler)}</div>
                    <div className="text-[13px] font-semibold text-gray-800 mb-1 font-serif">
                      {ilk}{uyeler.length > 1 ? ` – ${ilk + uyeler.length - 1}` : ''}. soruları aşağıdaki bilgilere göre cevaplayınız.
                    </div>
                    {kok && (
                      <div className="font-serif text-[15px] leading-relaxed text-gray-900 border border-gray-300 rounded px-3 py-2 mb-3">
                        <LatexParagraphs>{kok}</LatexParagraphs>
                        {(String(gercek.diyagram_tikz || '').trim() || gercek.gorsel_url) && (
                          <KitapcikSekil tikz={String(gercek.diyagram_tikz || '').trim() ? gercek.diyagram_tikz : null}
                            gorsel={gercek.gorsel_url} olcek={olcekOku(gercek.sekil_olcek)} />
                        )}
                      </div>
                    )}
                    {uyeler.map((u, i) => (
                      <div key={u.id || u.public_no} className="flex gap-2 mb-3">
                        <span className="font-bold font-serif text-[15px] shrink-0">{ilk + i}.</span>
                        <div className="min-w-0 flex-1">
                          {u.__eksik
                            ? <div className="text-xs italic text-gray-500">#{u.public_no} bu sayfada değil — açmak için tıklayın.</div>
                            : <SoruGovde s={u} kok={kok} sekilGoster={!kok} sekilCiz={sekilCiz} />}
                        </div>
                      </div>
                    ))}
                  </article>
                );
              }
              const secili = seciliIds && seciliIds.has(o.id);
              return (
                <article key={o.id} data-kitapcik-soru={o.public_no} onClick={() => onSoruTikla(o)}
                  className={`mb-6 break-inside-avoid cursor-pointer rounded-md p-2 -mx-2 transition-colors ${t.soru} ${secili ? t.secili : ''}`}>
                  <div className="mb-1.5">{rozetler(o)}</div>
                  <div className="flex gap-2">
                    <span className="font-bold font-serif text-[15px] shrink-0">{numaralar.get(o.id)}.</span>
                    <div className="min-w-0 flex-1">
                      {topluMod && <span className="text-blue-600 mr-1">{secili ? '☑' : '☐'}</span>}
                      {o.soru_metni_latex != null || o.sik_a != null
                        ? <SoruGovde s={o} sekilCiz={sekilCiz} />
                        : <div className="text-xs italic text-gray-400">(soru metni yükleniyor…)</div>}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
          <footer className="px-6 sm:px-10 pb-4 pt-2 border-t border-gray-300 flex justify-center text-xs font-semibold text-gray-600">
            {si + 1}
          </footer>
          </div>
        </section>
      ))}
    </div>
  );
}
